'use client'

import * as React from 'react'
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form'
import { FormFieldContext } from '../../hooks/FormFieldContext'

/**
 * @group Components
 * 
 * @summary
 * <blockquote>A controlled form field wrapper that provides context for nested form components.</blockquote>
 * 
 * @template TFieldValues - The form's data shape
 * @template TName - The field name within the form values
 * @param props - Controller props from react-hook-form
 * 
 * @example
 * ```typescript
 * import { FormField } from '@shared/ui'
 * 
 * <FormField
 *   control={form.control}
 *   name="email"
 *   render={({ field }) => <Input {...field} />}
 * />
 * ```
 */
export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: ControllerProps<TFieldValues, TName>
) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
} 